import React, { useEffect, useState } from 'react';
import {
  Button,
  Divider,
  Dropdown,
  Input,
  Menu,
  Popconfirm,
  Table,
} from 'antd';
import {
  EditOutlined,
  QuestionCircleOutlined,
  DeleteOutlined,
  DownOutlined,
  PlusOutlined,
} from '@ant-design/icons';
import { connect, useDispatch, useSelector } from 'react-redux';
import CustomContent from '../../components/Content/CustomContent';
import { history } from '../../utils/history';
import { deleteUsersAction, getUsersAction } from '../../actions/UserActions';
import { DEFAULT_PAGE_SIZE, usersPaginationParams } from './Constants';
import Authorized from '../../components/Authorization/Authorized';
import { getDateTimeDisplayText } from '../../utils/dateUtils';
import { getUsernameAvatar } from '../../utils/userAvatars';
import { useTranslation } from 'react-i18next';

const { Search } = Input;

const UserList = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [filterParams, setFilterParams] = useState({
    ...usersPaginationParams,
  });

  const { users } = useSelector(store => ({
    users: store.userReducer.users,
  }));

  useEffect(() => {
    dispatch(getUsersAction(filterParams));
  }, [filterParams]);

  const onSelectChange = selected => {

    setSelectedRowKeys(selected);
  };

  const handleMultipleDelete = () => {
    dispatch(deleteUsersAction(selectedRowKeys.join(',')));
    setSelectedRowKeys([]);
  };

  const handleSingleDelete = () => {
    dispatch(deleteUsersAction(selectedUser.id));
    setSelectedRowKeys([]);
  };

  const handleFilterParams = filter => {
    setFilterParams({
      ...filterParams,
      ...filter,
    });
  };

  const handleSearch = value => {

    // const { filterParams } = this.state;
    handleFilterParams({ value });
  };

  const handleMenuClick = action => {

    if (action.key === 'edit') {
      const updatePath = '/updateUser/' + selectedUser.id;

      history.push(updatePath);
    }
    // if (action.key === 'delete') {
    //   handleSingleDelete();
    // }
  };

  const handleAddNew = () => {
    history.push('/addUser');
  };

  const handleTableChange = pagination => {


    setFilterParams({
      ...filterParams,
      page: pagination.current - 1,
      size: pagination.pageSize,
    });
  };

  const hasSelected = selectedRowKeys.length > 0;

  const menu = (
    <Authorized asset="user" operation="write">
      <Menu onClick={handleMenuClick}>
        <Menu.Item key="edit">
          <EditOutlined />
          {t('common.update')}
        </Menu.Item>
        <Menu.Item key="delete">
          <Popconfirm
            title={t('common.confirmation')}
            description={t('common.areYouSureToDelete')}
            placement="left"
            icon={<QuestionCircleOutlined style={{ color: 'red' }} />}
            onConfirm={() => handleSingleDelete()}
            okButtonProps={{ size: 'middle' }}
            okText={t('common.yes')}
            cancelButtonProps={{ size: 'middle' }}
            styles={{ body: { padding: 16 } }}
          >
            <DeleteOutlined />
            {t('common.delete')}
          </Popconfirm>
        </Menu.Item>
      </Menu>
    </Authorized >
  );

  const columns = [
    {
      title: '',
      dataIndex: 'name',
      key: 'id',
      render: (text, record) => getUsernameAvatar(record.username),
    },
    {
      title: t('common.name'),
      dataIndex: 'name',
      key: 'name',
      render: (text, record) => (
        // eslint-disable-next-line jsx-a11y/click-events-have-key-events,jsx-a11y/no-static-element-interactions
        <a
          onClick={() => {
            history.push('/updateUser/' + record.id);
          }}
        >
          {text}
        </a>
      ),
    },
    {
      title: t('common.email'),
      dataIndex: 'email',
      key: 'email',
    },
    {
      title: t('common.username'),
      dataIndex: 'username',
      key: 'username',
    },
    {
      title: t('common.userGroup'),
      dataIndex: 'type',
      key: 'userGroup',
    },
    {
      title: t('common.phoneNumber'),
      dataIndex: 'phoneNumber',
      key: 'phoneNumber',
    },
    {
      title: t('users.lastActive'),
      dataIndex: 'lastSeen',
      key: 'lastSeen',
      render: text => getDateTimeDisplayText(text),
    },
    {
      title: t('common.actions'),
      key: 'action',
      render: () => (
        <span>
          <Dropdown overlay={menu} trigger={['click']}>
            <a className="ant-dropdown-link" href="#">
              {t('common.actions')} <DownOutlined />
            </a>
          </Dropdown>
        </span>
      ),
    },
  ];

  const rowSelection = {
    selectedRowKeys,
    onChange: onSelectChange,
  };

  return (
    <CustomContent>
      <Search
        placeholder={t('common.search')}
        onSearch={value => handleSearch(value)}
        allowClear
        style={{ float: 'left', width: 300, marginBottom: 16 }}
      />
      <Authorized asset="user" operation="write">
        <Button
          icon={<PlusOutlined />}
          type="primary"
          style={{ float: 'right', marginBottom: 16 }}
          onClick={handleAddNew}
        >
          {t('common.add')}
        </Button>
        <Popconfirm
          title={t('common.sureToDelete')}
          icon={<QuestionCircleOutlined style={{ color: 'red' }} />}
          onConfirm={() => handleMultipleDelete()}
        >
          <Button
            icon={<DeleteOutlined />}
            disabled={!hasSelected}
            style={{ float: 'right', marginBottom: 16, marginRight: 16 }}
          >
            {t('common.delete')}
          </Button>
        </Popconfirm>
      </Authorized>
      <Divider />
      <Table
        columns={columns}
        dataSource={users.content}
        rowSelection={rowSelection}
        pagination={{
          defaultPageSize: DEFAULT_PAGE_SIZE,
          total: users.totalElements,
          showSizeChanger: true,
          pageSizeOptions: [
            '10',
            '20',
            '30',
            '40',
            '50',
            '100',
            '200',
            '500',
            '1000',
          ],
          showTotal: (total, range) => {
            return t('users.paginationRange', { range0: range[0], range1: range[1], total });
          },
        }}
        onChange={handleTableChange}
        rowKey={record => record.id}
        onRow={record => {
          return {
            onClick: () => setSelectedUser(record),
          };
        }}
      />
    </CustomContent>
  );
};

UserList.propTypes = {
  // users: PropTypes.array,
  // getUsersAction: PropTypes.func,
  // deleteUsersAction: PropTypes.func,
};

function mapStateToProps({ userReducer }) {
  const { users } = userReducer;
  return { users };
}
export default connect(mapStateToProps, {
  getUsersAction,
  deleteUsersAction,
})(UserList);
